import { useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import { useRouter } from "next/router";
import { useState } from "react";
import { UseFormReturn, useForm, useWatch } from "react-hook-form";

import PosLayout from "../components/layouts/posLayout.comp";
import OrderFilters from "../components/orders/orderFilters.comp";
import { OrderPaymentStatus } from "../components/orders/orderPaymentStatus.comp";
import OrderStats from "../components/orders/orderStats.comp";
import { OrderStatus } from "../components/orders/orderStatus.comp";
import { PickupDatePicker } from "../components/orders/pickupDatePicker.comp";
import PageNav from "../components/reuseables/pageNav.comp";
import { formatCurrency } from "../core/currency.core";
import { loadOrders } from "../core/order.core";
import { useTranslation } from "../core/translation.core";
import { Order, OrderListFilter } from "../types/order";
import Link from "next/link";
import CalendarIcon from "../icons/calendar.icon";

export default function PosHistoryPage() {
  return (
    <PosLayout title="Order History">
      <Content />
    </PosLayout>
  );
}

function Content() {
  const { msg, t } = useTranslation();
  const { query, isReady } = useRouter();
  const [search, setSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [pickupStatus, setPickupStatus] = useState("NA");
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>(undefined);
  const [showPanel, setShowPanel] = useState(false);
  const [showOrderStats, setShowOrderStats] = useState(false);
  const [currentPaymentStatus, setCurrentPaymentStatus] = useState<string | undefined>(undefined);
  const gridStyle = "md:grid md:grid-cols-[150px_150px_120px_150px_150px_100px_auto] [@media(min-width:1048px)]:grid-cols-8";

  const togglePaymentStatus = (status: string) => {
    setCurrentPaymentStatus((prevStatus) => (prevStatus === status ? undefined : status));
    setPaymentStatus((prevStatus) => (prevStatus === status ? undefined : status));
  };

  const PickupStatusArr = [
    {
      name: t(msg.order.orderHistory),
      status: "NA",
    },
    {
      name: t(msg.order.awaitingPickup),
      status: "awaiting_pickup",
    },
    {
      name: t(msg.order.pickupCompleted),
      status: "pickup_completed",
    },
  ];

  const methods = useForm<OrderListFilter>({
    defaultValues: {
      date: {
        from: format(addDays(new Date(), -365), "yyyy-MM-dd"),
        to: format(new Date(), "yyyy-MM-dd"),
      },
      orderStatuses: [],
      page: Number(query.page) || 1,
      pageSize: 20,
    },
  });

  const [pageSize, date, orderStatuses] = useWatch({
    control: methods.control,
    name: ["pageSize", "date", "orderStatuses"],
  });

  const orderQueryKey = ["orders", query.page, pageSize, orderStatuses, search, productSearch, date, pickupStatus, paymentStatus];
  const { data: orders } = useQuery({
    enabled: isReady,
    queryFn: (context) => {
      return loadOrders({
        date,
        orderStatuses,
        paging: {
          page: context.queryKey[1] as number,
          size: context.queryKey[2] as number,
        },
        paymentStatus,
        pickupStatus,
        search,
        productSearch,
      });
    },
    queryKey: orderQueryKey,
  });

  return (
    <main className="space-y-8 dark:text-white">
      <section className="block lg:flex items-center justify-between space-y-4 lg:space-y-0">
        <h1 className="text-3xl font-bold">{t(msg.order.orders)}</h1>

        <div className="flex flex-wrap gap-x-2 gap-y-2 lg:gap-3 items-center">
          <button
            type="button"
            id="order-summary-toggle"
            onClick={() => setShowOrderStats((v) => !v)}
            className="rounded-md text-sm px-4 py-2 bg-white dark:bg-her-darkblue2 dark:text-white flex items-center gap-2 min-w-[120px] justify-center border border-transparent dark:border-pos-black-blue shadow-sm"
          >
            {showOrderStats ? t(msg.order.hideOrderSummary) : t(msg.order.showOrderSummary)}
          </button>

          <OrderFilters methods={methods} />
        </div>
      </section>

      {showOrderStats && <OrderStats date={date} />}

      <section className="space-y-8">
        <div className="flex gap-3 font-bold text-xl bg-white dark:bg-her-darkblue2 dark:text-white p-3 ten">
          {PickupStatusArr.map(({ name, status }) => (
            <button
              key={name}
              id="orderHistory"
              data-btn-active={pickupStatus === status}
              className={`${
                pickupStatus === status ? "dark:bg-blue-100 border-2 dark:text-her-blue border-her-blue " : "bg-pos-black"
              } rounded-md px-4 py-1 text-sm font-semibold`}
              onClick={() => setPickupStatus(status)}
            >
              {name}
            </button>
          ))}
        </div>

        <section className="bg-white dark:bg-pos-dark pt-5 pb-1 px-2 rounded-lg">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex gap-2 items-center cursor-pointer dark:border-pos-black-blue border shadow-sm p-4 w-full">
              <span>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="10.084" cy="10.084" r="5.5" stroke="#B3B3B3" strokeWidth="1.5" />
                  <path d="M18.334 18.334L15.584 15.584" stroke="#B3B3B3" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </span>

              <input
                id="order-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="text"
                placeholder={t(msg.order.searchOrderId)}
                className="bg-transparent w-full outline-none"
              />
            </div>

            <div className="flex gap-2 items-center cursor-pointer dark:border-pos-black-blue border shadow-sm p-4 w-full">
              <span>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="10.084" cy="10.084" r="5.5" stroke="#B3B3B3" strokeWidth="1.5" />
                  <path d="M18.334 18.334L15.584 15.584" stroke="#B3B3B3" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </span>

              <input
                id="product-search"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                type="text"
                placeholder={t(msg.order.searchProduct)}
                className="bg-transparent w-full outline-none"
              />
            </div>
          </div>

          <div id="order-list" data-type="section" className="space-y-8 pt-4 pb-8 overflow-x-scroll md:text-sm lg:text-base">
            <div className={"hidden px-2 py-4 rounded-md  dark:text-white gap-2 " + gridStyle}>
              <div>{t(msg.order.orderId)}</div>
              <div>{t(msg.order.date)}</div>
              <div>{t(msg.order.customer)}</div>

              <div className="relative">
                <div className="flex gap-2 items-center">
                  <span>{t(msg.order.paymentStatus)}</span>

                  <button onClick={() => setShowPanel(!showPanel)}>{vector}</button>
                </div>

                {showPanel && (
                  <section className="absolute top-10 left-10 w-[180px] z-50 shadow-md bg-white text-black font-medium text-sm rounded-md">
                    <button
                      className="hover:bg-[#EBF2FD] hover:text-blue-600 opacity-80 rounded-md p-2 w-full"
                      onClick={() => togglePaymentStatus("deposit")}
                    >
                      <div className="flex gap-2 items-center">
                        <span>{currentPaymentStatus === "deposit" ? checkedIcon : uncheckedIcon}</span>

                        <span>{t(msg.order.partiallyPaid)}</span>
                      </div>
                    </button>

                    <button
                      className="hover:bg-[#EBF2FD] hover:text-blue-600 opacity-80 rounded-md p-2 w-full"
                      onClick={() => togglePaymentStatus("paid")}
                    >
                      <div className="flex gap-2 items-center">
                        <span>{currentPaymentStatus === "paid" ? checkedIcon : uncheckedIcon}</span>

                        <span>{t(msg.order.fullyPaid)}</span>
                      </div>
                    </button>

                    <button
                      className="hover:bg-[#EBF2FD] hover:text-blue-600 opacity-80 rounded-md p-2 w-full"
                      onClick={() => togglePaymentStatus("unpaid")}
                    >
                      <div className="flex gap-2 items-center">
                        <span>{currentPaymentStatus === "refunded" ? checkedIcon : uncheckedIcon}</span>

                        <span>{t(msg.order.refund)}</span>
                      </div>
                    </button>

                    <button
                      className="hover:bg-[#EBF2FD] hover:text-blue-600 opacity-80 rounded-md p-2 w-full"
                      onClick={() => togglePaymentStatus("")}
                    >
                      <div className="flex gap-2 items-center">
                        <span>{currentPaymentStatus === undefined ? checkedIcon : uncheckedIcon}</span>
                        <span>{t(msg.order.allTime)}</span>
                      </div>
                    </button>
                  </section>
                )}
              </div>

              <div>{t(msg.order.orderStatus)}</div>
              <div>{t(msg.order.pickupStatus)}</div>
              <div>{t(msg.order.total)}</div>
            </div>

            {orders?.list?.map((order) => (
              <OrderItem key={order.id} order={order} gridStyle={gridStyle} />
            ))}
          </div>
        </section>
      </section>

      {/* @ts-ignore */}
      <Paging methods={methods} total={orders?.size} />
    </main>
  );
}

function OrderItem({ order, gridStyle }: { order: Order; gridStyle: string }) {
  const { msg, t } = useTranslation();
  const [showDatePicker, setShowDatePicker] = useState(false);

  if (!order) {
    return null;
  }

  return (
    <section id="order-list__item" className="border-b pb-4 cursor-pointer">
      <div className={"space-y-3 px-4 md:px-1 gap-2 " + gridStyle}>
        <div className="flex gap-1  text-her-blue items-center">
          <Link href={`/new-order/checkout?orderId=${order.id}`} className="order-link text-base underline-offset-4 underline">
            {order.orderNumber} <span>({order.products.length})</span>
          </Link>
        </div>

        <div className="flex justify-between items-center text-sm md:text-her-darkgray">
          <Tag>{t(msg.order.date)}</Tag>
          <div className="flex gap-1 md:block">
            <p>{format(new Date(order.orderAt), "yyyy-MM-dd")}</p>
            <p>{format(new Date(order.orderAt), "HH:mm:ss")}</p>
          </div>
        </div>

        <div className="flex justify-between items-center text-sm truncate md:text-her-darkgray">
          <Tag>{t(msg.order.customer)}</Tag>
          <p className="truncate">
            {order.customer?.lastName}&nbsp;{order.customer?.firstName}
          </p>
        </div>

        <div className="flex justify-between items-center text-sm md:text-xs">
          <Tag>{t(msg.order.paymentStatus)}</Tag>
          {/* @ts-ignore */}
          <OrderPaymentStatus status={order?.paymentStatus} />
        </div>

        <div className="flex justify-between items-center text-sm md:text-xs">
          <Tag>{t(msg.order.orderStatus)}</Tag>
          <OrderStatus status={order.status} pickup={order?.isPickedUp} />
        </div>

        <div className="flex justify-between items-center">
          <Tag>{t(msg.order.pickupStatus)}</Tag>
          <div className="flex items-center gap-2">
            <div>
              <h1>{order.pickupStatus === "awaiting_pickup" && t(msg.order.awaitingPickup)}</h1>
              <h1>{order.pickupStatus === "pickup_completed" && t(msg.order.pickupCompleted)}</h1>
              <h1>{order.pickupStatus === "NA" && t(msg.order.NA)}</h1>
              <p>{order.pickupDate ? format(new Date(order.pickupDate), "d / MM / yyyy") : ""}</p>
            </div>
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDatePicker(!showDatePicker);
                }}
                className={`p-2 rounded-md text-sm font-medium ${
                  order.pickupStatus === "pickup_completed"
                    ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    : "bg-her-blue text-white hover:bg-blue-600"
                }`}
              >
                <CalendarIcon />
              </button>

              <PickupDatePicker isOpen={showDatePicker} onClose={() => setShowDatePicker(false)} order={order} />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center  md:col-span-1 md:justify-end">
          <Tag>{t(msg.order.total)}</Tag>
          <p className="flex flex-col">
            <span className="text-xl">{formatCurrency(order.total, order.products[0].currency)}</span>
          </p>
        </div>
      </div>
    </section>
  );
}

function Tag({ children }: { children: string }) {
  return <span className="md:hidden text-[#999999] text-sm">{children}: </span>;
}

function Paging({ methods, total }: { methods: UseFormReturn<OrderListFilter, any>; total: number }) {
  const router = useRouter();
  const [pageSize] = useWatch({
    control: methods.control,
    name: ["pageSize"],
  });

  //  @ts-ignore
  return <PageNav page={Number(router.query.page) || 1} size={pageSize} total={total} pathname="/orders" query={router.query} />;
}

const vector = (
  <svg width="7" height="13" viewBox="0 0 7 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.52474 0.730466L5.57256 3.18785C6.00678 3.70891 5.63625 4.5 4.95798 4.5L1.70738 4.5C1.02911 4.5 0.658587 3.70891 1.09281 3.18785L3.14063 0.730466C3.24058 0.610528 3.42479 0.610528 3.52474 0.730466Z"
      fill="#959595"
    />
    <path
      d="M3.14128 12.2695L1.09346 9.81215C0.659238 9.29109 1.02976 8.5 1.70803 8.5L4.95863 8.5C5.6369 8.5 6.00743 9.29109 5.57321 9.81215L3.52539 12.2695C3.42544 12.3895 3.24123 12.3895 3.14128 12.2695Z"
      fill="#959595"
    />
  </svg>
);

const checkedIcon = (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8.5" cy="8.5" r="7.5" fill="white" stroke="#3478F7" strokeWidth="2" />
    <circle cx="8.5" cy="8.5" r="4.5" fill="#3478F7" />
  </svg>
);

const uncheckedIcon = (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8.5" cy="8.5" r="8" fill="white" stroke="#B3B3B3" />
  </svg>
);
